/**
 * /recurring-series/:id — staff view of one weekly daycare series
 * (migration 0096): the rule, its generated visits, pause/resume, and
 * cancel-future.
 */
import { useAuthContext } from '@/auth/useAuthContext';
import { StatusPill } from '@/components/StatusPill';
import { formatPriceMinor } from '@/lib/format';
import {
  type RecurringSeriesDto,
  type SeriesInstanceDto,
  formatDay,
  sessionLabel,
  weekdaysLabel,
} from '@/lib/recurrence';
import { cn } from '@/lib/utils';
import { AlertTriangle, ArrowLeft, Loader2, Pause, Play, Repeat, XCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  cancelRecurringSeriesFuture,
  errorMessage,
  getRecurringSeries,
  setRecurringSeriesStatus,
} from './api';

export function RecurringSeriesPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { state } = useAuthContext();
  const currency = state.me?.activeAccount?.currency ?? 'GBP';

  const [series, setSeries] = useState<RecurringSeriesDto | null>(null);
  const [instances, setInstances] = useState<SeriesInstanceDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const r = await getRecurringSeries(id);
      setSeries(r.series);
      setInstances(r.instances);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleStatus = async () => {
    if (!series || busy) return;
    setBusy(true);
    setError(null);
    try {
      const next = series.status === 'paused' ? 'active' : 'paused';
      const r = await setRecurringSeriesStatus(series.id, next);
      setSeries(r.series);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const cancelFuture = async () => {
    if (!series || busy) return;
    setBusy(true);
    setError(null);
    try {
      await cancelRecurringSeriesFuture(series.id);
      setConfirmCancel(false);
      await load();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const upcoming = instances.filter((i) => i.status === 'pending' || i.status === 'confirmed');

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-10 md:px-10 md:py-14">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mb-6 flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground press"
      >
        <ArrowLeft className="size-4" strokeWidth={1.75} aria-hidden />
        Back
      </button>

      {loading && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="size-5 animate-spin" strokeWidth={1.75} />
          Loading series…
        </div>
      )}

      {!loading && error && !series && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-6 py-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {series && (
        <>
          <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
            Recurring daycare
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <h1 className="font-heading text-4xl font-medium tracking-tight md:text-5xl">
              {weekdaysLabel(series.weekdays)}
            </h1>
            <StatusPill status={series.status} className="capitalize" />
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            {sessionLabel(series.sessionType)} · from{' '}
            <span className="num">{formatDay(series.startDate, true)}</span>
            {series.endDate ? (
              <>
                {' '}
                until <span className="num">{formatDay(series.endDate, true)}</span>
              </>
            ) : (
              ' · open-ended (12 weeks kept booked)'
            )}
            {series.dropOffTime && (
              <>
                {' '}
                · drop-off <span className="num">{series.dropOffTime}</span>
              </>
            )}
            {series.pickUpTime && (
              <>
                {' '}
                · pick-up <span className="num">{series.pickUpTime}</span>
              </>
            )}
            {' · '}
            <Link to={`/customers/${series.customerId}`} className="text-primary hover:underline">
              View customer
            </Link>
          </p>

          {series.status !== 'ended' && (
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={toggleStatus}
                disabled={busy}
                className="flex items-center gap-1.5 rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium press disabled:opacity-50"
              >
                {series.status === 'paused' ? (
                  <Play className="size-4" strokeWidth={1.75} />
                ) : (
                  <Pause className="size-4" strokeWidth={1.75} />
                )}
                {series.status === 'paused' ? 'Resume series' : 'Pause series'}
              </button>
              {!confirmCancel ? (
                <button
                  type="button"
                  onClick={() => setConfirmCancel(true)}
                  disabled={busy}
                  className="flex items-center gap-1.5 rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium text-destructive press disabled:opacity-50"
                >
                  <XCircle className="size-4" strokeWidth={1.75} />
                  Cancel future visits
                </button>
              ) : (
                <span className="flex items-center gap-2 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  Cancel <span className="num font-medium">{upcoming.length}</span> upcoming visit
                  {upcoming.length === 1 ? '' : 's'} and end the series?
                  <button
                    type="button"
                    onClick={cancelFuture}
                    disabled={busy}
                    className="rounded-md bg-destructive px-3 py-1 text-xs font-medium text-white press disabled:opacity-50"
                  >
                    Yes, cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmCancel(false)}
                    className="rounded-md px-2 py-1 text-xs font-medium press"
                  >
                    Keep
                  </button>
                </span>
              )}
            </div>
          )}
          <p className="mt-2 text-xs text-muted-foreground">
            Pausing stops new weeks being added; already-booked visits stay. To change a single
            visit, edit that booking directly.
          </p>

          {error && (
            <p className="mt-4 flex items-center gap-1.5 text-sm text-destructive">
              <AlertTriangle className="size-4 shrink-0" strokeWidth={1.75} />
              {error}
            </p>
          )}

          <section className="mt-8 rounded-xl bg-surface ring-1 ring-border elevate">
            <div className="flex items-center gap-2 px-6 py-4">
              <Repeat className="size-4 text-primary" strokeWidth={1.75} aria-hidden />
              <h2 className="text-sm font-medium">Visits</h2>
              <span className="text-xs text-muted-foreground">
                <span className="num">{instances.length}</span> generated ·{' '}
                <span className="num">{upcoming.length}</span> upcoming
              </span>
            </div>
            {instances.length === 0 ? (
              <p className="border-t border-border px-6 py-6 text-sm text-muted-foreground">
                No visits generated yet.
              </p>
            ) : (
              <ul className="divide-y divide-border border-t border-border">
                {instances.map((i, idx) => (
                  <li
                    key={i.bookingId}
                    className="rise flex items-center gap-4 px-6 py-3"
                    style={{ '--i': idx } as React.CSSProperties}
                  >
                    <Link
                      to={`/bookings/${i.bookingId}`}
                      className={cn(
                        'num w-40 text-sm font-medium hover:underline',
                        i.status === 'cancelled' && 'text-muted-foreground line-through',
                      )}
                    >
                      {formatDay(i.date, true)}
                    </Link>
                    <span className="flex-1 text-sm text-muted-foreground">
                      {sessionLabel(i.sessionType)}
                      {i.burnSource && i.burnSource !== 'pay_per_visit' && (
                        <span className="ml-2 text-xs">· {i.burnSource}</span>
                      )}
                    </span>
                    <span className="num text-sm">
                      {i.totalMinor > 0 ? formatPriceMinor(i.totalMinor, currency) : '—'}
                    </span>
                    <StatusPill status={i.status} />
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
